// --- Flutter Specific Utilities ---
const cachedUserId = createCachedValue('getUserId');
const cachedEmail = createCachedValue('getEmail');
const cachedUser = createCachedValue('getUser');
const cachedAccessToken = createCachedValue('requestAccessToken');
const cachedRefreshToken = createCachedValue('refreshToken');
/**
 * Helper function to call a Flutter handler via flutter_inappwebview.
 * @param handlerName The name of the Flutter handler to call.
 * @param args Arguments to pass to the Flutter handler.
 * @returns A promise that resolves with the handler's result or rejects if unavailable.
 */
const callFlutterHandler = (handlerName, ...args) => {
    // Ensure flutter_inappwebview is available before calling
    if (window.flutter_inappwebview?.callHandler) {
        return window.flutter_inappwebview.callHandler(handlerName, ...args);
    }
    // Return a rejected promise if the handler is not available
    return Promise.reject(new Error(`Flutter handler '${handlerName}' is not available`));
};
if (typeof window !== 'undefined') {
    // Listen for the Flutter InAppWebView platform ready event to preload cached values
    window.addEventListener('flutterInAppWebViewPlatformReady', async function () {
        // eslint-disable-next-line
        console.log('Flutter InAppWebView is ready, preloading cached values...');
        try {
            await preloadAllCached();
            // eslint-disable-next-line
            console.log('Finished preloading cached values.');
        }
        catch (error) {
            console.error('Error during preloading cached values:', error);
        }
    });
}
/**
 * Preloads all defined cached values from Flutter asynchronously.
 * Logs any errors encountered during the preloading process.
 */
async function preloadAllCached() {
    const preloadPromises = [
        cachedUserId.preload(),
        cachedEmail.preload(),
        cachedUser.preload(),
        cachedAccessToken.preload(),
        cachedRefreshToken.preload(),
    ];
    const results = await Promise.allSettled(preloadPromises);
    results.forEach((result, index) => {
        if (result.status === 'rejected') {
            const handlerNames = [
                'getUserId',
                'getEmail',
                'getUser',
                'requestAccessToken',
                'refreshToken',
            ];
            console.error(`Failed to preload ${handlerNames[index]}:`, result.reason);
        }
    });
}
/**
 * Sets up global callback functions (resolveGetAuthData, rejectGetAuthData,
 * getAuthDataSuccess, getAuthDataError) on the window object
 * to handle asynchronous results from native getAuthData calls.
 */
function createAndroidAuthDataCallback() {
    if (typeof window == 'undefined')
        return;
    window.resolveGetAuthData = null;
    window.rejectGetAuthData = null;
    window.getAuthDataSuccess = (response) => {
        const { data } = JSON.parse(response);
        if (window.resolveGetAuthData) {
            window.resolveGetAuthData(data);
            window.resolveGetAuthData = null;
        }
        // eslint-disable-next-line
        console.log('auth-data', JSON.stringify(data));
    };
    window.getAuthDataError = (error) => {
        if (window.rejectGetAuthData) {
            window.rejectGetAuthData(error);
            window.rejectGetAuthData = null;
        }
        // eslint-disable-next-line
        console.log('auth-data-error', error);
    };
}
/**
 * Extracts the PMM app version from the user agent string.
 * Assumes the format 'androidmobile PMM/<version> ...'
 * @returns The extracted version number, or 0 if not found.
 */
function getVersion() {
    const originalUserAgent = window.navigator.userAgent;
    const prefix = 'androidmobile PMM/';
    let version = 0;
    if (originalUserAgent.includes(prefix)) {
        version = Number(originalUserAgent.split(prefix).join('').split(' ')[0]);
    }
    return version;
}
function nativeAppHandler() {
    return androidHandler();
}
/**
 * Detects the environment (Flutter InAppWebView, Native Android Webview, or neither)
 * and returns the appropriate handler object for interacting with the native mobile app.
 * It prioritizes Flutter InAppWebView if available.
 *
 * @returns {NativeAppHandler} An object conforming to the NativeAppHandler interface.
 */
function androidHandler() {
    // Native Android Webview if available
    if (isNativeAndroidWebViewAvailable()) {
        // --- Native Android Webview Handler ---
        return {
            enabled: true,
            guruTokenEnabled: !!window.AndroidHandler?.getAuthData,
            getVersion,
            getAuthToken: () => window.AndroidHandler?.getAuthToken?.() ?? null,
            getServerAuthCode: () => window.AndroidHandler?.getServerAuthCode?.() ?? null,
            requestAccessToken: () => window.AndroidHandler?.requestAccessToken?.() ?? null,
            getEmail: () => window.AndroidHandler?.getEmail?.() ?? null,
            getUser: () => window.AndroidHandler?.getUser?.() ?? null,
            getUserId: () => window.AndroidHandler?.getUserId?.() ?? null,
            refreshToken: () => window.AndroidHandler?.refreshToken?.() ?? null,
            backToApp: () => window.AndroidHandler?.backToApp?.() ?? null,
            shareToolkitUrl: (textToCopy) => window.AndroidHandler?.shareToolkitUrl?.(textToCopy) ?? null,
            getAuthData: (refresh = false) => {
                window.AndroidHandler?.getAuthData?.(refresh);
                return new Promise((resolve, reject) => {
                    window.resolveGetAuthData = resolve;
                    window.rejectGetAuthData = reject;
                });
            },
            requestLogin: () => window.AndroidHandler?.requestLogin?.() ?? null,
        };
    }
    // Flutter InAppWebView if available
    if (isFlutterInAppWebViewAvailable()) {
        // --- Flutter InAppWebView Handler ---
        return createFlutterHandler();
    }
    // If neither Flutter nor Native Android handlers are available
    return {
        enabled: false,
        guruTokenEnabled: false,
        getVersion,
        getAuthToken: () => null,
        getServerAuthCode: () => null,
        requestAccessToken: () => null,
        getEmail: () => null,
        getUser: () => null,
        getUserId: () => null,
        refreshToken: () => null,
        backToApp: () => null,
        shareToolkitUrl: () => null,
        getAuthData: () => null,
        requestLogin: () => null,
    };
}
// --- Flutter InAppWebView Specific Functions ---
// Note: These functions are only used when Flutter InAppWebView is detected.
/**
 * Creates the NativeAppHandler object specifically for the Flutter InAppWebView environment.
 * This uses cached values for some properties and calls Flutter handlers directly for actions.
 *
 * @returns {NativeAppHandler} The handler object for Flutter interaction.
 */
function createFlutterHandler() {
    // Preload cached values when Flutter handler is created
    preloadAllCached();
    return {
        enabled: true,
        guruTokenEnabled: true,
        getVersion,
        getAuthToken: () => null,
        getServerAuthCode: () => null,
        requestAccessToken: () => cachedAccessToken.value,
        getEmail: () => cachedEmail.value,
        getUser: () => cachedUser.value,
        getUserId: () => cachedUserId.value,
        refreshToken: () => cachedRefreshToken.value,
        backToApp: () => {
            callFlutterHandler('backToApp').catch((error) => {
                console.error('Error calling backToApp from Flutter:', error);
            });
        },
        shareToolkitUrl: (textToCopy) => {
            callFlutterHandler('shareToolkitUrl', textToCopy).catch((error) => {
                console.error('Error calling shareToolkitUrl from Flutter:', error);
            });
        },
        getAuthData: (refresh = false) => {
            callFlutterHandler('getAuthData', refresh);
            return new Promise((resolve, reject) => {
                window.resolveGetAuthData = resolve;
                window.rejectGetAuthData = reject;
            });
        },
        requestLogin: () => callFlutterHandler('requestLogin'),
    };
}
/**
 * Checks if the Flutter InAppWebView JavaScript interface is available on the window.
 * @returns {boolean} True if flutter_inappwebview and its callHandler exist, false otherwise.
 */
function isFlutterInAppWebViewAvailable() {
    return (typeof window !== 'undefined' &&
        !!window.flutter_inappwebview &&
        !!window.flutter_inappwebview.callHandler);
}
/**
 * Checks if the Native Android Webview is available on the window.
 * @returns {boolean} True if window.AndroidHandler exists, false otherwise.
 */
function isNativeAndroidWebViewAvailable() {
    return typeof window !== 'undefined' && !!window.AndroidHandler;
}
/**
 * Creates a cached value object for a specific Flutter handler.
 * Provides a `preload` method to fetch the value asynchronously and stores it.
 *
 * @template T The expected type of the cached value.
 * @param {string} handlerName The name of the Flutter handler to get the value from.
 * @returns {CachedValue<T>} An object with the cached value and a preload function.
 */
function createCachedValue(handlerName) {
    const cache = {
        value: null,
        preload: async () => {
            try {
                const result = await callFlutterHandler(handlerName);
                cache.value = result;
            }
            catch (e) {
                console.error(`Error preloading ${handlerName}:`, e);
                throw e;
            }
        },
    };
    return cache;
}

export { androidHandler, callFlutterHandler, createAndroidAuthDataCallback, getVersion, nativeAppHandler };
